import Splide, { type Splide as SplideInstance } from "@splidejs/splide";
import { Grid } from "@splidejs/splide-extension-grid";
import { sliderBreakpoints } from "../constant/breakpoint";

type PaginationData = {
  items: Array<{ button: HTMLButtonElement }>;
};

function getSplideElement(wrapper: Element) {
  return wrapper.querySelector(".splide") as HTMLElement | null;
}

const variantArrowOverhang = 15;

function clipBounds(element: HTMLElement) {
  let left = 0;
  let right = window.innerWidth;
  let node = element.parentElement;

  while (node) {
    const style = getComputedStyle(node);
    if (/(hidden|clip|auto|scroll)/.test(style.overflowX)) {
      const rect = node.getBoundingClientRect();
      left = Math.max(left, rect.left);
      right = Math.min(right, rect.right);
    }
    node = node.parentElement;
  }

  return { left, right };
}

function placeOutsideArrows(
  area: HTMLElement,
  prev: HTMLElement | null,
  next: HTMLElement | null,
  options: { rotatePrev: boolean; verticalCenter: boolean },
) {
  if (!prev || !next) return;

  const bounds = clipBounds(area);
  const rect = area.getBoundingClientRect();
  const prevOut = Math.max(
    0,
    Math.min(variantArrowOverhang, rect.left - bounds.left - 2),
  );
  const nextOut = Math.max(
    0,
    Math.min(variantArrowOverhang, bounds.right - rect.right - 2),
  );
  const y = options.verticalCenter ? ", -50%" : ", 0";
  const prevRotate = options.rotatePrev ? " rotate(180deg)" : "";

  prev.style.transform = `translate(${-prevOut}px${y})${prevRotate}`;
  next.style.transform = `translate(${nextOut}px${y})`;
}

function placeVariantArrows(splideEl: HTMLElement) {
  placeOutsideArrows(
    splideEl,
    splideEl.querySelector(".splide__arrow--prev"),
    splideEl.querySelector(".splide__arrow--next"),
    { rotatePrev: true, verticalCenter: false },
  );
}

function initProductMediaGalleryArrows() {
  document
    .querySelectorAll<HTMLElement>(".p-product-media-gallery__thumbnails")
    .forEach((area) => {
      const place = () => {
        placeOutsideArrows(
          area,
          area.querySelector(".slider-button--prev"),
          area.querySelector(".slider-button--next"),
          { rotatePrev: false, verticalCenter: true },
        );
      };

      place();
      window.addEventListener("resize", place);
      new ResizeObserver(place).observe(area);
    });
}

function addProgressBarToActiveButton(data: PaginationData) {
  for (const item of data.items) {
    item.button.querySelector(".splide__progress")?.remove();
  }

  const activeButton = data.items
    .map((item) => item.button)
    .find((button) => button.classList.contains("is-active"));

  if (!activeButton) return;

  const progress = document.createElement("div");
  progress.className = "splide__progress";
  progress.style.width = "0%";

  const bar = document.createElement("div");
  bar.className = "splide__progress__bar";

  progress.appendChild(bar);
  activeButton.appendChild(progress);
}

function resetProgressBar(splide: SplideInstance) {
  const activeBar = splide.root.querySelector<HTMLElement>(
    ".splide__pagination__page.is-active .splide__progress",
  );
  if (activeBar) {
    activeBar.style.width = "0%";
  }

  const autoplay = splide.Components.Autoplay;
  if (!autoplay) return;

  autoplay.pause();
  autoplay.play();
}

function updateProgressBarWidth(splide: SplideInstance, rate: number) {
  const activeBar = splide.root.querySelector<HTMLElement>(
    ".splide__pagination__page.is-active .splide__progress",
  );
  if (!activeBar) return;

  activeBar.style.width = `${rate * 100}%`;
}

function initSpotlightBanners() {
  document.querySelectorAll(".js-spotlight-banner").forEach((node) => {
    const wrapper = node as HTMLElement;
    const splideEl = getSplideElement(wrapper);
    if (!splideEl) return;

    const autoplay = wrapper.dataset.autoplay === "true";
    const interval = parseInt(wrapper.dataset.interval ?? "5000", 10);

    const splide = new Splide(splideEl, {
      type: "loop",
      mediaQuery: "min",
      perPage: 1,
      perMove: 1,
      gap: "12px",
      heightRatio: 1,
      padding: { left: "20px", right: "20px" },
      arrows: false,
      pagination: true,
      autoplay,
      interval,
      pauseOnHover: false,
      pauseOnFocus: true,
      resetProgress: true,
      reducedMotion: { autoplay: "pause", speed: 0 },
      // 768px以上はメインビジュアルを430px四方で固定する。画面幅を変えてもサイズは変えない。
      breakpoints: {
        768: {
          fixedWidth: "430px",
          fixedHeight: "430px",
          heightRatio: 0,
          focus: "center",
          padding: { left: "0px", right: "0px" },
        },
      },
    });

    splide.on("pagination:mounted", (data: PaginationData) => {
      addProgressBarToActiveButton(data);
    });

    splide.on("pagination:updated", (data: PaginationData) => {
      addProgressBarToActiveButton(data);
    });

    if (autoplay) {
      splide.on("autoplay:playing", (rate: number) => {
        updateProgressBarWidth(splide, rate);
      });
    }

    splide.on("dragged", () => {
      resetProgressBar(splide);
    });

    splide.mount();
  });
}

function initCampaignSliders() {
  document.querySelectorAll(".js-splide-campaign").forEach((node) => {
    const splideEl = getSplideElement(node);
    if (!splideEl) return;

    const splide = new Splide(splideEl, {
      type: "slide",
      arrows: true,
      perPage: 5,
      perMove: 1,
      gap: "20px",
      padding: { right: "0%" },
      pagination: false,
      drag: true,
      snap: true,
      trimSpace: false,
      reducedMotion: { speed: 0 },
      breakpoints: {
        768: {
          perPage: 1,
          padding: { right: "44%" },
          arrows: false,
          gap: "10px",
        },
      },
    });

    splide.mount();
  });
}

function initStaffPicksSliders() {
  document.querySelectorAll(".js-staff-picks-slider").forEach((node) => {
    const splideEl = getSplideElement(node);
    if (!splideEl) return;

    const splide = new Splide(splideEl, {
      type: "loop",
      mediaQuery: "min",
      perPage: 1,
      padding: { left: "41px", right: "41px" },
      gap: "7px",
      arrows: false,
      pagination: false,
      drag: true,
      snap: true,
      trimSpace: false,
      reducedMotion: { speed: 0 },
      breakpoints: {
        768: {
          type: "slide",
          perPage: 4,
          perMove: 1,
          padding: 0,
          gap: "7px",
          arrows: true,
        },
      },
    });

    splide.mount();
  });
}

function initSenmonContentsSliders() {
  document.querySelectorAll(".js-senmon-contents").forEach((node) => {
    const splideEl = getSplideElement(node);
    if (!splideEl) return;

    const splide = new Splide(splideEl, {
      type: "slide",
      perPage: 4,
      gap: "20px",
      padding: { right: "0" },
      arrows: false,
      pagination: false,
      drag: true,
      snap: true,
      trimSpace: false,
      reducedMotion: { speed: 0 },
      breakpoints: {
        768: {
          perPage: 1,
          padding: { right: "32%" },
          gap: "15px",
        },
      },
    });

    splide.mount();
  });
}

function initTopicsGuideSliders() {
  document.querySelectorAll(".js-topics-guide").forEach((node) => {
    const splideEl = getSplideElement(node);
    if (!splideEl) return;

    const splide = new Splide(splideEl, {
      type: "slide",
      grid: {
        rows: 1,
        cols: 6,
        gap: { row: "22px", col: "22px" },
      },
      gap: "22px",
      arrows: false,
      pagination: false,
      drag: true,
      snap: true,
      trimSpace: false,
      reducedMotion: { speed: 0 },
      breakpoints: {
        768: {
          grid: {
            rows: 2,
            cols: 2,
            gap: { row: "16px", col: "10px" },
          },
          gap: "10px",
          padding: { right: "12%" },
        },
      },
    });

    splide.mount({ Grid });
  });
}

function initSearchCategoryCollectionSliders() {
  document
    .querySelectorAll(".js-search-category-collection")
    .forEach((node) => {
      const splideEl = getSplideElement(node);
      if (!splideEl) return;

      const splide = new Splide(splideEl, {
        type: "slide",
        padding: { right: "8%" },
        grid: {
          rows: 3,
          cols: 3,
          gap: { row: "12px", col: "10px" },
        },
        gap: "10px",
        arrows: false,
        pagination: false,
        drag: true,
        snap: true,
        trimSpace: false,
        reducedMotion: { speed: 0 },
        mediaQuery: "min",
        breakpoints: {
          768: {
            destroy: true,
          },
        },
      });

      splide.mount({ Grid });
    });
}

function initBuyTogetherCarousel() {
  document.querySelectorAll(".js-splide-buy-together").forEach((node) => {
    const splideEl = getSplideElement(node);
    if (!splideEl) return;

    const splide = new Splide(splideEl, {
      type: "slide",
      mediaQuery: "min",
      arrows: true,
      perPage: 1,
      perMove: 1,
      gap: "5px",
      padding: "16px",
      pagination: false,
      drag: true,
      snap: true,
      trimSpace: false,
      reducedMotion: { speed: 0 },
      breakpoints: {
        [sliderBreakpoints.md]: {
          padding: { left: "0", right: "20%" },
        },
        [sliderBreakpoints.xl]: {
          padding: { left: "0", right: "36%" },
        },
      },
    });

    splide.mount();
  });
}

function initProductRelatedBlogCarousel() {
  document.querySelectorAll(".js-splide-related-blog").forEach((node) => {
    const splideEl = getSplideElement(node);
    if (!splideEl) return;

    const splide = new Splide(splideEl, {
      type: "slide",
      arrows: true,
      perPage: 2,
      perMove: 1,
      gap: "15px",
      padding: { left: "0", right: "40px" },
      pagination: false,
      drag: true,
      snap: true,
      trimSpace: false,
      reducedMotion: { speed: 0 },
      breakpoints: {
        767: {
          perPage: 1,
          padding: { left: "0", right: "34%" },
        },
      },
    });

    const alignArrows = () => {
      const media = splideEl.querySelector<HTMLElement>(
        ".p-product-related-blog__media",
      );
      const arrows = splideEl.querySelector<HTMLElement>(".splide__arrows");
      if (!media || !arrows) return;

      arrows.style.top = "0px";
      arrows.style.height = `${media.getBoundingClientRect().height}px`;
    };

    splideEl.querySelectorAll("img").forEach((img) => {
      img.addEventListener("load", alignArrows);
    });
    splide.on("mounted", alignArrows);
    splide.on("resized", alignArrows);
    splide.mount();
  });
}

function initRelatedVariantProductsSliders() {
  document
    .querySelectorAll(".js-splide-related-variant-products")
    .forEach((node) => {
      const splideEl = getSplideElement(node);
      if (!splideEl) return;

      const splide = new Splide(splideEl, {
        type: "slide",
        mediaQuery: "min",
        arrows: true,
        perPage: 3,
        perMove: 1,
        gap: "10px",
        padding: { right: "32px" },
        pagination: false,
        drag: true,
        snap: true,
        trimSpace: false,
        reducedMotion: { speed: 0 },
        breakpoints: {
          [sliderBreakpoints.sm]: {
            perPage: 4,
            gap: "10px",
            padding: { right: "24px" },
          },
          [sliderBreakpoints.md]: {
            perPage: 2,
            gap: "16px",
            padding: { right: "16px" },
          },
          [sliderBreakpoints.lg]: {
            perPage: 3,
            gap: "16px",
            padding: { right: "16px" },
          },
          [sliderBreakpoints.xl]: {
            perPage: 4,
            gap: "16px",
            padding: { right: "32px" },
          },
        },
      });

      const placeArrows = () => placeVariantArrows(splideEl);
      splide.on("mounted", placeArrows);
      splide.on("resized", placeArrows);
      splide.mount();
    });
}

export function initCarousels() {
  initProductMediaGalleryArrows();
  initSpotlightBanners();
  initCampaignSliders();
  initStaffPicksSliders();
  initSenmonContentsSliders();
  initTopicsGuideSliders();
  initSearchCategoryCollectionSliders();
  initBuyTogetherCarousel();
  initProductRelatedBlogCarousel();
  initRelatedVariantProductsSliders();
}
